/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  isDevMode,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {AppEnvironmentConfig} from "@app/app.environment.config";
import {
  RegistryContextInfo,
  RegistryContexts
} from "@app/models/constants/registry-constants";
import {
  SIDE_MENU_MAIN,
  SIDE_MENU_NOTIFICATION_CENTER,
} from "@app/models/constants/side-menu.constants";
import {UserData} from "@app/models/user-data";
import {TabService} from "@app/services/tab.service";
import {CommonState} from "@app/store/common/common.state";
import {MenuController, PopoverController} from "@ionic/angular";
import {Patient} from "@modules/doctor-panel/models/patient";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {ContestoPaziente, PasswordAuthenticatorService, TrecApiError} from "@trec/api";
import {TrecAuthStoreService, TrecSitesService} from "@trec/api/utils";
import {combineLatest, Observable, Subscription} from "rxjs";
import {debounceTime, map} from "rxjs/operators";
import {PushManagerService} from "../../services/push-manager.service";
import {AccountQuickMenuComponent} from "../account-quick-menu/account-quick-menu.component";
import {ExternalToolsListComponent} from "../external-tools-list/external-tools-list.component";
import {MobileAppListComponent} from "../mobile-app-list/mobile-app-list.component";

// declare var TogetherJS: any;

@Component({
  selector: "app-trec-toolbar",
  templateUrl: "./trec-toolbar.component.html",
  styleUrls: ["./trec-toolbar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TrecToolbarComponent implements OnInit, OnDestroy {
  /**
   * Observable containing flags indicating whether logged user is admin.
   */
  @Select(CommonState.isAdmin) readonly isAdmin$: Observable<boolean | undefined>;
  @Select(CommonState.isAuthenticated) readonly isAuthenticated$: Observable<boolean>;
  @Select(CommonState.isPhysician) readonly isDoctor$: Observable<boolean | undefined>;
  @Select(CommonState.patient) readonly currentPatient$: Observable<Patient | null>;
  @Select(CommonState.currentPatientAllContexts) readonly currentPatientAllContexts$: Observable<ContestoPaziente[] | null>;
  @Select(CommonState.isPatientPage) readonly isPatientPage$: Observable<boolean>;
  @Select(CommonState.patientContext) readonly patientContext$: Observable<
    RegistryContexts[] | null | undefined
  >;

  @Select(hasActionsExecuting()) readonly _globalLoading$: Observable<boolean>;
  readonly globalLoading$: Observable<boolean>;

  // @Select(actionsExecuting()) readonly globalActionExecuting$: Observable<ActionsExecutingStateModel>;

  /**
   * Push notification status
   */
  readonly pushStatus$ = this.pushManager.pushStatus$;

  /**
   * Current user data.
   *
   */
  userData?: UserData | null;

  /**
   * the source of logo
   */
  srcLogo: string | undefined;

  /**
   * patient context
   */
  patientContext: string[] = [];

  contextInfo: {[context: string]: RegistryContextInfo} = {};

  /**
   * Flag for devMode.
   */
  isDevMode = isDevMode();

  showPatientInfo$: Observable<{item: Patient | null; active: boolean;}>

  readonly SIDE_MENU_MAIN = SIDE_MENU_MAIN;
  readonly SIDE_MENU_NOTIFICATION_CENTER = SIDE_MENU_NOTIFICATION_CENTER;

  private readonly subscr = new Subscription();

  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
    private trecSites: TrecSitesService,
    private authStore: TrecAuthStoreService,
    private popoverCtrl: PopoverController,
    private pushManager: PushManagerService,
    private menuCtrl: MenuController,
    private passwordAuth: PasswordAuthenticatorService,
    private tabService: TabService
  ) {
    this.globalLoading$ = this._globalLoading$.pipe(debounceTime(200));
  }

  ngOnInit() {
    // if (!environment.production) {
    //   this.globalActionExecuting$.subscribe(state => {
    //     console.log('>>>', state, state ? Object.values(state).reduce((a, b) => a + b, 0) : 0);
    //   });
    // }
    this.subscr.add(

      this.currentPatientAllContexts$.subscribe({
        next: (data) => {
          if (!data) {
            return this.patientContext = []
          }
          const contextInfoLocalStorage = localStorage.getItem("CONTEXTS_INFO");
          this.contextInfo = contextInfoLocalStorage != null ? JSON.parse(contextInfoLocalStorage) : undefined;

          this.patientContext = data.filter(item => item.current).map(item => item.id)

        }, error: () => {
          this.patientContext = [];
          this.contextInfo = {}
        }
      }))


    // this.subscr.add(
    //   this.patientContext$.subscribe((ctx) => {
    //     this.contextInfo = CONTEXTS_INFO;
    //     this.patientContext = ctx;

    //     if (!this.patientContext) {
    //       const contextLocalStorage = localStorage.getItem("patientContext");
    //       this.patientContext =
    //         contextLocalStorage != null ? JSON.parse(contextLocalStorage) : undefined;
    //     }
    //     if (Object.keys(this.contextInfo).length === 0) {
    //       const contextInfoLocalStorage = localStorage.getItem("CONTEXTS_INFO");
    //       this.contextInfo =
    //         contextInfoLocalStorage != null ? JSON.parse(contextInfoLocalStorage) : undefined;
    //     }
    //     this.cd.markForCheck();
    //   })
    // );

    this.srcLogo = AppEnvironmentConfig.srcLogo;

    // Get current authenticated user data
    this.subscr.add(
      this.store.select(CommonState.loggedUserData).subscribe((userData) => {
        this.userData = userData;
        this.cd.markForCheck();
      })
    );

    this.showPatientInfo$ = combineLatest([
      this.currentPatient$,
      this.isPatientPage$
    ]).pipe(
      map(([item, active]) => ({
        item,
        active
      }))
    );
  }

  /**
   * Do login redirecting user to the authentication application.
   */
  onLogin() {
    this.trecSites.goToAuthenticationPage();
  }

  onAdminPanel() {
    this.trecSites.goToAdministrationPage();
  }

  onGoToApp(appName: string) {
    window.open(`${location.origin}/${appName}`, "_blank");
  }

  onGoToExternalService(serviceUrl: string) {
    window.open(serviceUrl, "_blank");
  }

  onLeaveFeedback() { }

  async onOpenAccounQuickMenu(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: AccountQuickMenuComponent,
      cssClass: "app-popover-account-quick-menu",
      showBackdrop: false,
      event,
    });
    await popover.present();
  }

  async onOpenAppsList(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: MobileAppListComponent,
      showBackdrop: false,
      event,
      cssClass: "app-popover-app-list",
    });
    await popover.present();
  }

  async onOpenExternalServicesList(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: ExternalToolsListComponent,
      showBackdrop: false,
      event,
      cssClass: "app-popover-app-list",
    });
    await popover.present();
  }

  onDevLoginComplete({jwt, trecUserId}: {jwt: string; trecUserId: string}) {
    this.authStore.saveAuthInfo({token: jwt, trecUserId});
    //location.reload();
  }

  onDevLogin({user, pass}: {user: string; pass: string}) {
    this.passwordAuth.login({username: user, password: pass}).subscribe(
      async (res) => {
        this.authStore.saveAuthInfo({token: res.JWT, trecUserId: res.trecUserId});
        location.reload();
      },
      (err: TrecApiError) => {
        this.cd.markForCheck();
      }
    );
  }

  async onToggleMainMenu() {
    // Since there are multiple "left" menus, if another is open, this must be enabled before opening
    await this.menuCtrl.enable(true, SIDE_MENU_MAIN);
    await this.menuCtrl.toggle(SIDE_MENU_MAIN);
  }
  // async onStartTogetherJS() {
  //   // Load togetherjs: (see: https://togetherjs.com)
  //   await this.externalLoader.loadExternalScript('https://togetherjs.com/togetherjs-min.js');
  //   // TODO the main togetherjs hub has been dismissed and cannot be used anymore
  //   window['TogetherJSConfig_hubBase'] = '';

  //   // Run togetherjs
  //   TogetherJS();
  // }

  setAnagraficaPatient() {
    this.tabService.setActiveTabId("patient-profile");
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();
  }
}
